import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { faCheckCircle, faExclamationCircle, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { HeadsetManufacturerType } from 'src/app/services/headset/headset-manufacturer-type';
import { ChangeHeadsetManufacturer } from 'src/app/store/local-storage/local-storage-settings/audio-devices-settings.actions';
import { AudioDevicesSettingsSelectors } from 'src/app/store/local-storage/local-storage-settings/audio-devices-settings.selectors';
import { JabraService } from '../../../../../services/headset/jabra.service';
import { PlantronicsService } from '../../../../../services/headset/plantronics.service';
import { SennheiserService } from '../../../../../services/headset/sennheiser.service';

@Component({
   selector: 'samweb-headset-manufacturer-selector',
   templateUrl: './headset-manufacturer-selector.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeadsetManufacturerSelectorComponent implements OnInit, OnDestroy {
   public selectedManufacturer$: Observable<HeadsetManufacturerType>;
   public isSelectedHeadsetAttached$: Observable<boolean>;
   public deviceFoundIcon: IconDefinition = faCheckCircle;
   public deviceNotFoundIcon: IconDefinition = faExclamationCircle;

   private destroy$ = new Subject();

   public headsetManufacturers$ = of(HeadsetManufacturerType.values());

   constructor(
      private store: Store,
      public jabraService: JabraService,
      public plantronicsService: PlantronicsService,
      public sennheiserService: SennheiserService
   ) {}

   ngOnInit() {
      this.selectedManufacturer$ = this.store.select(AudioDevicesSettingsSelectors.getHeadsetManufacturer);

      this.isSelectedHeadsetAttached$ = this.selectedManufacturer$.pipe(
         switchMap((selMan) => {
            switch (selMan) {
               case HeadsetManufacturerType.JABRA:
                  return this.jabraService.attachedHeadset$;
               case HeadsetManufacturerType.PLANTRONICS:
                  return this.plantronicsService.attachedHeadset$;
               case HeadsetManufacturerType.SENNHEISER:
                  return this.sennheiserService.attachedHeadset$;
               case HeadsetManufacturerType.ALL:
                  return combineLatest([
                     this.jabraService.attachedHeadset$.pipe(startWith()),
                     this.plantronicsService.attachedHeadset$.pipe(startWith()),
                     this.sennheiserService.attachedHeadset$.pipe(startWith()),
                  ]).pipe(
                     map((x) => {
                        return _.flatten(x);
                     })
                  );
               default:
                  return of([]);
            }
         }),
         map((attachedHeadsetObs) => {
            return attachedHeadsetObs.length > 0;
         }),
         takeUntil(this.destroy$)
      );
   }

   ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
   }

   public onSelectionChange(manufacturer: HeadsetManufacturerType) {
      this.store.dispatch(new ChangeHeadsetManufacturer(manufacturer));
   }
}
